import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Save, Lock, User, Mail, MapPin, Building, Briefcase, CalendarDays, Users } from 'lucide-react';

export default function ProfilePage() {
  const { user } = useAuth();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState('');
  const [location, setLocation] = useState('');
  const [reportingTo, setReportingTo] = useState('');
  const [saving, setSaving] = useState(false);

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [changingPw, setChangingPw] = useState(false);

  const loadProfile = useCallback(async () => {
    try {
      const res = await api.get('/auth/me');
      setProfile(res.data);
      setName(res.data.name || '');
      setLocation(res.data.location || '');
      setReportingTo(res.data.reporting_to || '');
    } catch {
      toast.error('Failed to load profile');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadProfile(); }, [loadProfile]);

  const handleSaveProfile = async () => {
    if (!name.trim()) { toast.error('Name is required'); return; }
    setSaving(true);
    try {
      await api.put('/employees/profile/update', { name, location, reporting_to: reportingTo });
      toast.success('Profile updated');
      loadProfile();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Update failed');
    } finally {
      setSaving(false);
    }
  };

  const handleChangePassword = async () => {
    if (!currentPassword || !newPassword) { toast.error('Fill all password fields'); return; }
    if (newPassword !== confirmPassword) { toast.error('Passwords do not match'); return; }
    if (newPassword.length < 6) { toast.error('Password must be at least 6 characters'); return; }
    setChangingPw(true);
    try {
      await api.put('/employees/profile/password', { current_password: currentPassword, new_password: newPassword });
      toast.success('Password changed successfully');
      setCurrentPassword(''); setNewPassword(''); setConfirmPassword('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Password change failed');
    } finally {
      setChangingPw(false);
    }
  };

  if (loading) return <div className="text-sm text-muted-foreground">Loading profile...</div>;

  return (
    <div data-testid="profile-page">
      <div className="mb-6">
        <h2 className="text-2xl font-heading font-bold text-foreground tracking-tight">My Profile</h2>
        <p className="text-sm text-muted-foreground font-body mt-1">View and update your personal information</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Profile Card */}
        <div className="border border-border bg-card rounded-sm p-6">
          <div className="flex flex-col items-center text-center">
            <div className="w-16 h-16 rounded-sm bg-[#002FA7] flex items-center justify-center text-white text-2xl font-heading font-bold mb-4">
              {profile?.name?.[0]?.toUpperCase()}
            </div>
            <h3 className="font-heading font-semibold text-foreground text-lg">{profile?.name}</h3>
            <p className="text-sm text-muted-foreground">{profile?.designation}</p>
            <Badge variant="secondary" className="mt-2 rounded-sm text-xs">{profile?.department}</Badge>
          </div>

          <div className="mt-6 space-y-3 text-sm">
            <div className="flex items-center gap-3 text-muted-foreground">
              <Mail className="w-4 h-4 text-muted-foreground shrink-0" strokeWidth={1.5} />
              <span className="truncate">{profile?.email}</span>
            </div>
            <div className="flex items-center gap-3 text-muted-foreground">
              <Briefcase className="w-4 h-4 text-muted-foreground shrink-0" strokeWidth={1.5} />
              <span className="font-mono text-xs">{profile?.employee_id}</span>
            </div>
            <div className="flex items-center gap-3 text-muted-foreground">
              <CalendarDays className="w-4 h-4 text-muted-foreground shrink-0" strokeWidth={1.5} />
              <span>Joined {profile?.date_of_joining}</span>
            </div>
            <div className="flex items-center gap-3 text-muted-foreground">
              <MapPin className="w-4 h-4 text-muted-foreground shrink-0" strokeWidth={1.5} />
              <span>{profile?.location || 'Not set'}</span>
            </div>
            <div className="flex items-center gap-3 text-muted-foreground">
              <Users className="w-4 h-4 text-muted-foreground shrink-0" strokeWidth={1.5} />
              <span>Reports to: {profile?.reporting_to || 'Not set'}</span>
            </div>
          </div>
        </div>

        {/* Edit Profile */}
        <div className="border border-border bg-card rounded-sm p-6">
          <h3 className="font-heading font-semibold text-foreground mb-4">
            <User className="w-4 h-4 inline mr-2" strokeWidth={1.5} />
            Edit Profile
          </h3>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-widest text-muted-foreground">Name</Label>
              <Input value={name} onChange={e => setName(e.target.value)} className="rounded-sm" data-testid="profile-name-input" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-widest text-muted-foreground">Location</Label>
              <Input value={location} onChange={e => setLocation(e.target.value)} placeholder="e.g., Mumbai" className="rounded-sm" data-testid="profile-location-input" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-widest text-muted-foreground">Reporting To</Label>
              <Input value={reportingTo} onChange={e => setReportingTo(e.target.value)} placeholder="Manager name" className="rounded-sm" data-testid="profile-reporting-input" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-widest text-muted-foreground">Email</Label>
              <Input value={profile?.email || ''} disabled className="rounded-sm bg-muted text-muted-foreground" />
              <p className="text-[10px] text-muted-foreground">Contact admin to change email</p>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-widest text-muted-foreground">Department</Label>
              <Input value={profile?.department || ''} disabled className="rounded-sm bg-muted text-muted-foreground" />
              <p className="text-[10px] text-muted-foreground">Contact admin to change department</p>
            </div>
            <Button onClick={handleSaveProfile} disabled={saving} className="bg-[#002FA7] hover:bg-[#002585] rounded-sm w-full" data-testid="save-profile-btn">
              <Save className="w-4 h-4 mr-2" strokeWidth={1.5} />
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>
        </div>

        {/* Change Password */}
        <div className="border border-border bg-card rounded-sm p-6">
          <h3 className="font-heading font-semibold text-foreground mb-4">
            <Lock className="w-4 h-4 inline mr-2" strokeWidth={1.5} />
            Change Password
          </h3>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-widest text-muted-foreground">Current Password</Label>
              <Input type="password" value={currentPassword} onChange={e => setCurrentPassword(e.target.value)} className="rounded-sm" data-testid="current-password-input" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-widest text-muted-foreground">New Password</Label>
              <Input type="password" value={newPassword} onChange={e => setNewPassword(e.target.value)} className="rounded-sm" data-testid="new-password-input" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-widest text-muted-foreground">Confirm New Password</Label>
              <Input type="password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} className="rounded-sm" data-testid="confirm-password-input" />
            </div>
            <Button onClick={handleChangePassword} disabled={changingPw} variant="outline" className="rounded-sm w-full" data-testid="change-password-btn">
              <Lock className="w-4 h-4 mr-2" strokeWidth={1.5} />
              {changingPw ? 'Changing...' : 'Change Password'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
